/**
 * The onboarding plan's map lock (`spec-onboarding-3d-plan-map-lock`, R1 and R2).
 *
 * Pure functions over the Mapbox map: record every setting the lock changes, lock, put back exactly
 * what was recorded, and the height scale that collapses the twin into a plan (transition B). The
 * order in which the projection and max pitch are set belongs to `usePlanMapLock`, not here.
 *
 * Started from the camera spike's `planLockSpike.tsx` (held only in
 * `docs/specs/evidence/onboarding-3d-plan-spike-ortho-camera/spike.patch.txt`), with the projection
 * read through public API, the one private read isolated, the canvas `tabindex`, and the height
 * scale tied to the plan view's state instead of to pitch alone.
 */

/** Max zoom while locked: 250 px/m, the planner's scale. */
export const PLAN_MAX_ZOOM = 24;
/** The plan rests with every height scaled to this. */
export const PLAN_REST_HEIGHT_SCALE = 0.02;
/** Duration of the tilt into and out of the plan view. A camera move, not a UI motion token. */
export const PLAN_TILT_MS = 1000;
/** Mapbox 3.7 draws the orthographic camera only below this pitch. */
export const MAPBOX_SWITCH_PITCH = 15;
/**
 * In the free camera, heights are full from this pitch (`spec-onboarding-persistent-scene-lock-poses`,
 * R1.1). A camera value, not a UI motion token.
 */
export const FREE_FULL_HEIGHT_PITCH = 30;

export type CameraProjection = 'perspective' | 'orthographic';

type Handler = { enable(): void; disable(): void; isEnabled(): boolean };

/** The part of the Mapbox map the lock reads and writes. A real `mapboxgl.Map` satisfies it. */
export interface PlanLockMap {
  getCamera(): { 'camera-projection'?: CameraProjection } | null | undefined;
  setCamera(camera: { 'camera-projection': CameraProjection }): unknown;
  getMinPitch(): number;
  getMaxPitch(): number;
  getMinZoom(): number;
  getMaxZoom(): number;
  setMinPitch(v: number): unknown;
  setMaxPitch(v: number): unknown;
  setMinZoom(v: number): unknown;
  setMaxZoom(v: number): unknown;
  getPitch(): number;
  getCanvas(): HTMLElement;
  dragRotate: Handler;
  dragPan: Handler;
  scrollZoom: Handler;
  boxZoom: Handler;
  doubleClickZoom: Handler;
  keyboard: Handler;
  touchZoomRotate: Handler & { enableRotation(): void; disableRotation(): void };
  touchPitch: Handler;
}

const HANDLERS = [
  'dragRotate',
  'dragPan',
  'scrollZoom',
  'boxZoom',
  'doubleClickZoom',
  'keyboard',
  'touchZoomRotate',
  'touchPitch',
] as const;
type HandlerName = (typeof HANDLERS)[number];

export interface PlanLockSettings {
  projection: CameraProjection;
  minPitch: number;
  maxPitch: number;
  minZoom: number;
  maxZoom: number;
  /** The camera's pitch when the lock was taken; leaving returns to it. */
  pitch: number;
  handlers: Record<HandlerName, boolean>;
  touchRotation: boolean;
  canvasTabIndex: string | null;
}

let warnedTouchRotation = false;

/**
 * Whether touch rotation is on. Mapbox 3.7 has no public getter, so this is the lock's one private
 * read. Where the field is missing (a future Mapbox), it assumes rotation follows the handler.
 */
export const readTouchRotationEnabled = (map: PlanLockMap): boolean => {
  const disabled = (map.touchZoomRotate as unknown as { _rotationDisabled?: unknown })
    ._rotationDisabled;
  if (typeof disabled === 'boolean') return !disabled;
  if (!warnedTouchRotation) {
    warnedTouchRotation = true;
    console.warn(
      '[planMapLock] touchZoomRotate._rotationDisabled is missing; assuming touch rotation follows the handler',
    );
  }
  return map.touchZoomRotate.isEnabled();
};

export const readPlanLockSettings = (map: PlanLockMap): PlanLockSettings => {
  const handlers = {} as Record<HandlerName, boolean>;
  HANDLERS.forEach(h => {
    handlers[h] = map[h].isEnabled();
  });
  return {
    projection: map.getCamera()?.['camera-projection'] ?? 'perspective',
    minPitch: map.getMinPitch(),
    maxPitch: map.getMaxPitch(),
    minZoom: map.getMinZoom(),
    maxZoom: map.getMaxZoom(),
    pitch: map.getPitch(),
    handlers,
    touchRotation: readTouchRotationEnabled(map),
    canvasTabIndex: map.getCanvas().getAttribute('tabindex'),
  };
};

export interface PlanLockOptions {
  /**
   * False also turns off Mapbox's own pan, scroll zoom and touch zoom, for a host whose camera is
   * driven by something else — the 3D read-only plan follows its view model
   * (`spec-onboarding-3d-plan-plan-scene`, R6). Defaults to true: those three are left alone.
   */
  mapGestures?: boolean;
}

/**
 * Lock the controls. Pan, scroll zoom and the bearing are left alone unless `mapGestures` is false.
 * The projection and max pitch are not set here: `usePlanMapLock` sets them only once the camera is
 * flat.
 */
export const applyPlanLock = (
  map: PlanLockMap,
  { mapGestures = true }: PlanLockOptions = {},
): void => {
  map.setMaxZoom(PLAN_MAX_ZOOM);
  // The camera must be able to reach flat; a raised min pitch would also make max pitch 0 throw.
  if (map.getMinPitch() > 0) map.setMinPitch(0);
  map.dragRotate.disable();
  map.touchZoomRotate.disableRotation();
  map.touchPitch.disable();
  map.boxZoom.disable();
  map.doubleClickZoom.disable();
  map.keyboard.disable();
  if (!mapGestures) {
    map.dragPan.disable();
    map.scrollZoom.disable();
    map.touchZoomRotate.disable();
  }
  // Mapbox keeps its canvas focusable even with the keyboard handler off; a focus stop that does
  // nothing is worse than none (R5). Not focusable at all (renderer-switch R3.5): a `-1` canvas takes
  // a mouse press's focus, so its pane could only keep the keys by focusing itself from script, and
  // a scripted focus paints the keyboard ring. With no focusable canvas the press focuses the pane.
  map.getCanvas().removeAttribute('tabindex');
};

/** Put back exactly what `readPlanLockSettings` recorded. Nothing here is a default. */
export const restorePlanLock = (map: PlanLockMap, recorded: PlanLockSettings): void => {
  map.setCamera({ 'camera-projection': recorded.projection });
  // Mapbox refuses a min above the current max (and the reverse), so order each pair by direction.
  if (recorded.minPitch > map.getMaxPitch()) {
    map.setMaxPitch(recorded.maxPitch);
    map.setMinPitch(recorded.minPitch);
  } else {
    map.setMinPitch(recorded.minPitch);
    map.setMaxPitch(recorded.maxPitch);
  }
  if (recorded.minZoom > map.getMaxZoom()) {
    map.setMaxZoom(recorded.maxZoom);
    map.setMinZoom(recorded.minZoom);
  } else {
    map.setMinZoom(recorded.minZoom);
    map.setMaxZoom(recorded.maxZoom);
  }
  HANDLERS.forEach(h => {
    if (recorded.handlers[h]) map[h].enable();
    else map[h].disable();
  });
  if (recorded.touchRotation) map.touchZoomRotate.enableRotation();
  else map.touchZoomRotate.disableRotation();
  const canvas = map.getCanvas();
  if (recorded.canvasTabIndex === null) canvas.removeAttribute('tabindex');
  else canvas.setAttribute('tabindex', recorded.canvasTabIndex);
};

/**
 * `free` is `off` with heights following the tilt (`spec-onboarding-persistent-scene-lock-poses`,
 * R1): unlocked, the camera the person's.
 */
export type PlanViewState = 'off' | 'entering' | 'locked' | 'leaving' | 'free';

/**
 * A whole camera position for the lock to enter from or leave to (R2, R3). `pitch` is read only on
 * leaving: entering always ends flat.
 */
export interface PlanPose {
  center: { lng: number; lat: number };
  zoom: number;
  bearing: number;
  pitch?: number;
}

export interface HeightScaleInput {
  state: PlanViewState;
  /** The map's pitch now. */
  pitch: number;
  /** Pitch when the current enter or leave began. */
  startPitch: number;
  /** Height scale when it began: 1 from `off`, 0.02 from `locked`, anything between on a reversal. */
  startScale: number;
  /** Pitch the leave eases to. */
  targetPitch: number;
  /** The ease's eased progress, 0 to 1. */
  progress: number;
  /** The leave ends in `free`: its heights end on the free rule's value, not 1. */
  toFree?: boolean;
}

const clamp01 = (v: number) => Math.min(1, Math.max(0, v));

/** R1.1: flat at or below 15° of pitch, full at or above 30°, linear between. */
export const freeHeightScale = (pitch: number): number => {
  const rest = PLAN_REST_HEIGHT_SCALE;
  const t = clamp01((pitch - MAPBOX_SWITCH_PITCH) / (FREE_FULL_HEIGHT_PITCH - MAPBOX_SWITCH_PITCH));
  return rest + (1 - rest) * t;
};

/**
 * Transition B. Heights are full outside the plan view at any pitch, and 0.02 inside it. Entering,
 * they collapse with pitch and are flat by 15°, so Mapbox's switch to the orthographic camera
 * happens on a flat plan; leaving mirrors that. Where the tilt never crosses 15°, the collapse
 * follows the ease's progress instead. In `free`, heights follow the pitch (`freeHeightScale`), and a
 * leave into it ends on that rule's value at its target, so the hand-over never jumps.
 */
export const planHeightScale = (s: HeightScaleInput): number => {
  const rest = PLAN_REST_HEIGHT_SCALE;
  if (s.state === 'off') return 1;
  if (s.state === 'free') return freeHeightScale(s.pitch);
  if (s.state === 'locked') return rest;
  if (s.state === 'leaving') {
    const end = s.toFree ? freeHeightScale(s.targetPitch) : 1;
    if (s.progress >= 1) return end;
    // Rising to a target above 15°: from the switch pitch, or from wherever a reversed enter left
    // the camera, so the scale never jumps at the start. A camera already at or above its target
    // (a reversal from a steeper free tilt) follows the progress instead.
    if (s.targetPitch > MAPBOX_SWITCH_PITCH && s.startPitch < s.targetPitch) {
      const from = Math.max(MAPBOX_SWITCH_PITCH, s.startPitch);
      const t = clamp01((s.pitch - from) / (s.targetPitch - from));
      return s.startScale + (end - s.startScale) * t;
    }
    return s.startScale + (end - s.startScale) * clamp01(s.progress);
  }
  // Entering.
  if (s.startPitch > MAPBOX_SWITCH_PITCH) {
    const t = clamp01((s.pitch - MAPBOX_SWITCH_PITCH) / (s.startPitch - MAPBOX_SWITCH_PITCH));
    return rest + (s.startScale - rest) * t;
  }
  return s.startScale + (rest - s.startScale) * clamp01(s.progress);
};

/** A floor scales about its own height, so an upper floor never collapses onto the ground. */
export const scaleAboutHeight = (baseY: number, k: number) => ({
  positionY: baseY * (1 - k),
  scaleY: k,
});

/**
 * Mapbox's default ease, `cubic-bezier(0.25, 0.1, 0.25, 1)`. The hook passes it as `easeTo`'s
 * `easing` so it can read the ease's progress each frame, which Mapbox does not expose.
 */
export const planEase = (() => {
  const x1 = 0.25;
  const y1 = 0.1;
  const x2 = 0.25;
  const y2 = 1;
  const cx = 3 * x1;
  const bx = 3 * (x2 - x1) - cx;
  const ax = 1 - cx - bx;
  const cy = 3 * y1;
  const by = 3 * (y2 - y1) - cy;
  const ay = 1 - cy - by;
  const sampleX = (t: number) => ((ax * t + bx) * t + cx) * t;
  const sampleY = (t: number) => ((ay * t + by) * t + cy) * t;
  const slopeX = (t: number) => (3 * ax * t + 2 * bx) * t + cx;
  const solveX = (x: number) => {
    let t = x;
    for (let i = 0; i < 8; i++) {
      const err = sampleX(t) - x;
      if (Math.abs(err) < 1e-6) return t;
      const d = slopeX(t);
      if (Math.abs(d) < 1e-6) break;
      t -= err / d;
    }
    let lo = 0;
    let hi = 1;
    t = x;
    while (lo < hi) {
      const v = sampleX(t);
      if (Math.abs(v - x) < 1e-6) return t;
      if (x > v) lo = t;
      else hi = t;
      t = (hi - lo) / 2 + lo;
      if (hi - lo < 1e-7) break;
    }
    return t;
  };
  return (k: number) => (k <= 0 ? 0 : k >= 1 ? 1 : sampleY(solveX(k)));
})();
